/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import {
  ContentSwitcher,
  FormLabel,
  RadioButton,
  RadioButtonGroup,
  Switch,
  TextInput
} from '@carbon/react';
import { InformationFilled, TextFill } from '@carbon/react/icons';
import urlJoin from 'proper-url-join';
import { useCallback, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import PlusTag from '@ibm-watson/shared/modules/ia/components/plus-tag/PlusTag';
import { useIsNewIA } from '@ibm-watson/shared/modules/ia/hooks/useIsNewIA';
import {
  selectPlan,
  selectIsNewIa
} from '@ibm-watson/shared/modules/ia/store/instance/selectors';
import { ctaEvents } from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { useAppSelector } from '@ibm-watson/shared/store';
import { EnvironmentType } from '../../../../../ts/assistant';
import Link from '../../../../ia/components/link/Link';
import {
  getAssistantId,
  selectInstanceId
} from '../../../../ia/store/session/selectors';
import { getBranding } from '../../../../ia/util/branding';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { selectDraftIntegration } from '../../../store/channels/selectors';
import { SEGMENT } from '../../../trackers/trackEvents';
import {
  AI_THEME_OPTIONS,
  CARBON_THEME_OPTIONS,
  WEB_CHAT_COLOR_ACCENT,
  WEB_CHAT_COLOR_PRIMARY,
  WEB_CHAT_COLOR_SECONDARY,
  WEB_CHAT_HEIGHT,
  WEB_CHAT_WIDTH
} from '../../../util/validation/constants';
import { NAMESPACES, TOOLING_TYPES } from '../../../util/web-chat';
import { TabContent } from '../../common/tabbed-header/TabContent';
import PreviewWebChat from '../../preview-web-chat/PreviewWebChat';
import { links } from '../links';
import { StyleTab as messages } from '../messages';
import { FIELDS } from '../schema';
import { AvatarField } from './AvatarField';
import { ColorPicker } from './ColorPicker';

import './Style.scss';

interface StyleProps {
  /**
   * If this is the style tab from the initial onboarding process, we can adjust some of the copy on the page.
   */
  inOnboardingWizard?: boolean;
  /**
   *
   */
  tabname?: string;
  /**
   *
   */
  environmentId?: string;
  /**
   *
   */
  integrationId: string;
  /**
   *
   */
  readOnly?: boolean;
  /**
   *
   */
  onClose?: () => void;
}
export const Style = ({
  inOnboardingWizard,
  environmentId,
  integrationId,
  onClose = () => {},
  readOnly
}: StyleProps) => {
  const formMethods = useFormContext();
  const {
    errorMessageIntl,
    getFieldValue,
    registerInputField,
    registerToggleControllerField
  } = useFormHelpers(formMethods);
  const { control } = formMethods;

  const webChatContainer = useRef<HTMLDivElement | null>(null);

  const botNameChangeState = useState<boolean>(false);
  const widthChangeState = useState<boolean>(false);
  const heightChangeState = useState<boolean>(false);

  const plan = useAppSelector(selectPlan);
  const navigate = useNavigate();

  const isNewIA = useAppSelector(selectIsNewIa);
  const assistantId = useAppSelector(getAssistantId);
  const instanceId = useAppSelector(selectInstanceId);
  const previewPageURL = urlJoin(
    encodeURIComponent(instanceId),
    'assistants',
    assistantId,
    'preview'
  );

  const draftIntegration = useAppSelector(selectDraftIntegration);
  const renderSizeInformation =
    isNewIA && draftIntegration?.name === EnvironmentType.DRAFT;

  const onToggle = (value: boolean) => {
    ctaEvents.webChatBrandingToggle({
      category: 'Branding',
      resultValue: value
        ? SEGMENT.EVENTS.WEB_CHAT_BRANDING_ENABLED
        : SEGMENT.EVENTS.WEB_CHAT_BRANDING_DISABLED
    });
  };

  const handleOnClick = useCallback(
    evt => {
      evt.preventDefault();
      onClose();
      navigate(previewPageURL);
    },
    [navigate, onClose, previewPageURL]
  );

  const { formatMessage: t } = useIntl();
  const { productName } = getBranding();

  const subtitle = inOnboardingWizard
    ? t(messages.subtitleOnboarding)
    : t(messages.subtitle);

  return (
    <TabContent
      ImageComponent={() => <TextFill size={32} />}
      title={<FormattedMessage {...messages.title} />}
      subtitle={subtitle}
      learnMoreLink={
        useIsNewIA()
          ? links.learnMoreConfigLinkNewIA
          : links.learnMoreConfigLinkClassic
      }>
      <div className="Style-tab">
        <div className="TabContent__field-container">
          <TextInput
            {...registerInputField({
              field: FIELDS.BOT_NAME,
              labelText: messages.botNameLabel,
              changeState: botNameChangeState,
              readOnly
            })}
            id="integration_public_bot_name"
            className="TabContent__field"
          />
        </div>
        <div className="TabContent__field-container">
          <Controller
            control={control}
            name={FIELDS.THEME_CONFIG.USE_AI_THEME}
            render={({ field: { onChange, value } }) => {
              return (
                <RadioButtonGroup
                  className="Theme_radio-group"
                  disabled={readOnly}
                  name={FIELDS.THEME_CONFIG.USE_AI_THEME}
                  legendText={t(messages.aiThemeLabel)}
                  orientation="vertical"
                  valueSelected={
                    value ? AI_THEME_OPTIONS.ENABLED : AI_THEME_OPTIONS.DISABLED
                  }>
                  <RadioButton
                    id="theme_ai_theme_option_disabled"
                    value={AI_THEME_OPTIONS.DISABLED}
                    labelText={t(messages.aiThemeOptionDisabled)}
                    onClick={() => onChange(false)}
                  />
                  <RadioButton
                    id="theme_ai_theme_option_enabled"
                    value={AI_THEME_OPTIONS.ENABLED}
                    labelText={t(messages.aiThemeOptionEnabled)}
                    onClick={() => onChange(true)}
                  />
                </RadioButtonGroup>
              );
            }}
          />
        </div>
        <div className="TabContent__field-container">
          <Controller
            control={control}
            name={FIELDS.THEME_CONFIG.CARBON_THEME}
            render={({ field: { onChange, value } }) => {
              return (
                <>
                  <FormLabel>
                    <FormattedMessage {...messages.carbonThemeLabel} />
                  </FormLabel>
                  <ContentSwitcher
                    size={'md'}
                    onChange={({ name }) => onChange(name)}
                    selectedIndex={
                      value === CARBON_THEME_OPTIONS.LIGHT ? 0 : 1
                    }>
                    <Switch
                      index={0}
                      name={CARBON_THEME_OPTIONS.LIGHT}
                      text={t(messages.carbonThemeOptionLight)}
                      onClick={({ name }) => onChange(name)}
                    />
                    <Switch
                      index={1}
                      name={CARBON_THEME_OPTIONS.DARK}
                      text={t(messages.carbonThemeOptionDark)}
                      onClick={({ name }) => onChange(name)}
                    />
                  </ContentSwitcher>
                </>
              );
            }}
          />
        </div>
        {!getFieldValue(FIELDS.THEME_CONFIG.USE_AI_THEME) && (
          <>
            <div className="TabContent__divided-field-container">
              <div className="TabContent__field-container">
                <Controller
                  control={control}
                  name={FIELDS.CSS.PRIMARY_COLOR}
                  render={({ field: { onChange, value } }) => (
                    <ColorPicker
                      readOnly={readOnly}
                      id={FIELDS.CSS.PRIMARY_COLOR}
                      placeholder={WEB_CHAT_COLOR_PRIMARY}
                      labelText={t(messages.primaryLabel)}
                      helperText={t(messages.primaryHelperText)}
                      value={value}
                      onChange={onChange}
                      error={errorMessageIntl(FIELDS.CSS.PRIMARY_COLOR)}
                    />
                  )}
                />
              </div>
              <div className="TabContent__field-container">
                <Controller
                  control={control}
                  name={FIELDS.CSS.SECONDARY_COLOR}
                  render={({ field: { onChange, value } }) => (
                    <ColorPicker
                      readOnly={readOnly}
                      id={FIELDS.CSS.SECONDARY_COLOR}
                      placeholder={WEB_CHAT_COLOR_SECONDARY}
                      labelText={t(messages.secondaryLabel)}
                      helperText={t(messages.secondaryHelperText)}
                      value={value}
                      onChange={onChange}
                      error={errorMessageIntl(FIELDS.CSS.SECONDARY_COLOR)}
                    />
                  )}
                />
              </div>
            </div>
            <div className="TabContent__divided-field-container">
              <div className="TabContent__field-container">
                <Controller
                  control={control}
                  name={FIELDS.CSS.ACCENT_COLOR}
                  render={({ field: { onChange, value } }) => (
                    <ColorPicker
                      readOnly={readOnly}
                      id={FIELDS.CSS.ACCENT_COLOR}
                      placeholder={WEB_CHAT_COLOR_ACCENT}
                      labelText={t(messages.accentLabel)}
                      helperText={t(messages.accentHelperText)}
                      value={value}
                      onChange={onChange}
                      error={errorMessageIntl(FIELDS.CSS.ACCENT_COLOR)}
                    />
                  )}
                />
              </div>
            </div>
            <h5>
              <FormattedMessage {...messages.size} />
            </h5>
            {renderSizeInformation && (
              <div className="TabContent__inner-field TabContent__inner-field-body">
                <InformationFilled
                  size={16}
                  className="TabContent__info-icon"
                  aria-label={t(messages.sizeNote)}
                />
                <span>
                  <FormattedMessage {...messages.sizeNote} />{' '}
                  {!inOnboardingWizard && (
                    <>
                      <FormattedMessage {...messages.visitPreviewPageText} />
                      <Link href={previewPageURL} onClick={handleOnClick}>
                        <FormattedMessage {...messages.previewPageLinkText} />
                      </Link>
                      .
                    </>
                  )}
                </span>
              </div>
            )}
            <div className="TabContent__divided-field-container TabContent__SizeInputContainer">
              <div className="TabContent__field-container">
                <TextInput
                  {...registerInputField({
                    field: FIELDS.CSS.WIDTH,
                    labelText: messages.width,
                    placeholderText: WEB_CHAT_WIDTH,
                    changeState: widthChangeState,
                    readOnly
                  })}
                  disabled={inOnboardingWizard}
                  id="integration_public_chat_width"
                  className="TabContent__field"
                />
              </div>
              <div className="TabContent__field-container">
                <TextInput
                  {...registerInputField({
                    field: FIELDS.CSS.HEIGHT,
                    labelText: messages.height,
                    placeholderText: WEB_CHAT_HEIGHT,
                    changeState: heightChangeState,
                    readOnly
                  })}
                  disabled={inOnboardingWizard}
                  id="integration_public_chat_height"
                  className="TabContent__field"
                />
              </div>
            </div>
            {getFieldValue(FIELDS.BRANDING_CONFIG) && (
              <div className="TabContent__field-container">
                <h5>
                  {plan === 'lite' ? (
                    <div className="TabContent__field--disabled">
                      <FormattedMessage {...messages.brandingLabel} />
                      <PlusTag />
                    </div>
                  ) : (
                    <FormattedMessage {...messages.brandingLabel} />
                  )}
                </h5>
                <Controller
                  {...registerToggleControllerField({
                    field: FIELDS.BRANDING_CONFIG_IS_ON,
                    onToggle: (value: boolean) => {
                      onToggle(!value);
                    },
                    labelText: t(messages.enableBranding, { productName }),
                    ariaLabel: t(messages.enableBranding, { productName }),
                    size: 'sm',
                    readOnly: readOnly || plan === 'lite'
                  })}
                />
              </div>
            )}
          </>
        )}
        {isNewIA && (
          <div className="TabContent__field-container">
            <h5>
              <FormattedMessage {...messages.streamingLabel} />
            </h5>
            <Controller
              {...registerToggleControllerField({
                field: FIELDS.STREAMING.IS_ON,
                labelText: t(messages.enableStreaming),
                ariaLabel: t(messages.enableStreaming),
                size: 'sm',
                readOnly: readOnly
              })}
            />
          </div>
        )}
      </div>
      <div className="Style-tab__avatar">
        <Controller
          control={control}
          name={FIELDS.BOT_AVATAR_URL}
          render={({ field: { onChange, value } }) => (
            <AvatarField
              readOnly={readOnly}
              id="integration_bot_avatar_url"
              updateBotAvatarURL={onChange}
              defaultValue={value}
            />
          )}
        />
      </div>
      <div className="Style-tab__webchat">
        <PreviewWebChat
          environmentId={environmentId}
          integrationId={integrationId}
          containerRef={webChatContainer}
          namespace={NAMESPACES.CHANNEL_PAGE}
          removeDefaultFocus={true}
          showRestart={true}
          toolingType={TOOLING_TYPES.WEB_CHAT}
          useDraftConfig
          disableSessionHistory
        />
        <div className="container" ref={webChatContainer} />
      </div>
    </TabContent>
  );
};
